import React, { Component, useEffect } from 'react';
import _ from 'underscore';
import { renderReact } from './utils/reactRoot';
import { Router, Route, Switch } from 'react-router-dom';
import history from './utils/history';
import { Provider } from 'react-redux';
import pubSUb from 'PubSub';
import { bindActionCreators } from 'redux';
import MainCommunityContainerView from './views/reactViews/MainCommunityContainerView';
import AgendaContainer from './views/reactViews/AgendaView/AgendaContainer';
import MainUserProfileContainerView from './views/reactViews/MainUserProfileContainerView';
import MainInboxContainerView from './views/reactViews/MainInboxContainerView';
import MainSettingsContainerView from './views/reactViews/MainSettingsContainerView';
import MainChangeEmailContainerView from './views/reactViews/MainChangeEmailContainerView';
import MainApproveChangeEmailContainerView from './views/reactViews/MainApproveChangeEmailContainerView';
import FeatureToggleUtils from 'FeatureToggleUtils';
import LoaderView from './views/reactViews/LoaderView';
import storeFactory from './store/store';
import reactBackboneInit from './ReactBackboneInit';
import { validateLinkedInToken } from './actions/CommunityActions';
import WebSocket from 'models/WebSocket';
import BootstrapData from 'models/BootstrapData';
import 'iframe-resizer-content-window';
import $ from 'jquery';
import SpeakerContainer from './views/reactViews/AgendaView/SpeakerView/SpeakerContainer';
import SessionPageContainer from './views/reactViews/AgendaView/SessionPageContainer';
import withEmailLogin from './hocs/withEmailLogin';
import cls from 'classnames';
import {loadCustomCode} from './CustomCode';
import withMe from './withMe';
import { withInstanaPageTracking } from './hocs/withInstanaPageTracking';
import serviceConfig from 'config';
import { startSnowplowTrackingAction } from './actions/snowplowActions';
import { initializeSnowplowTrackerIfEnabled } from './utils/SnowplowUtils';
import useApplyCustomizations from './utils/useApplyCustomizations';
import {isBizzaboInternalWebsiteWhiteLabeled} from '@bizzabo/whitelabel-utils';
import { saveUrlParamsToSessionStorage } from './utils/saveUrlParamsToSessionStorage';
import { installScrollRestoration } from './utils/scrollRestoration';

const withTracking = (trackingOptions) =>
    Comp => withInstanaPageTracking({ ...trackingOptions, instanaKey: serviceConfig?.instanaKey})(Comp);

const webSocket = new WebSocket();
const initialState = window.__INITIAL_STATE__;
const store = storeFactory(initialState);
const event = BootstrapData('bootstrap-data-event');
const { template = '' } = BootstrapData('bootstrap-data-siteConf') || {};

const actions = bindActionCreators({
    validateLinkedInToken,
    startSnowplowTrackingAction,
}, store.dispatch);

const createRoutes = routes => {
    const isWL = isBizzaboInternalWebsiteWhiteLabeled(event);
    const wlPath = event.whitelabelPath;
    return _(routes)
        .chain()
        .pairs()
        .map(pair => {
            if (!isWL) {
                return pair;
            }
            const [name, url] = pair;
            const uniqueNamePlaceholder = /^:unique_name/;
            const wlPathWithoutLeadingSlash = wlPath.replace(/^\//, '');
            const realUrl = url.replace(uniqueNamePlaceholder, wlPathWithoutLeadingSlash)
                .replace(/^\//, '');
            return [name, realUrl];
        })
        .object()
        .value();
};

const routes = createRoutes({
    community: ':unique_name/community',
    person: ':unique_name/person/:id',
    inboxId: ':unique_name/inbox/:id',
    inbox: ':unique_name/inbox',
    settings: ':unique_name/settings',
    changeEmail: ':unique_name/settings/changeEmail',
    changeEmailApproval: ':unique_name/settings/changeEmailApproval',
    agenda: ':unique_name/agenda',
    sessionPage: ':unique_name/agenda/session/:id',
    speaker: ':unique_name/agenda/speakers/:id',
    notFound: '*'
});

FeatureToggleUtils.mergeFeatureToggleWithConfig();

function scrollParentIFrameToTop() {
    window.parentIFrame && window.parentIFrame.sendMessage('scrollToTop', '*');
}

function onRouteChange() {
    scrollParentIFrameToTop();
    pubSUb.publish('analytics:routeChanged');
}

// Adapted from react-router 3's onEnter on the community-family routes: run the
// iframe-resize messaging + LinkedIn token validation as a side effect when the
// route matches (v5 has no onEnter, so it lives in a wrapper effect below).
const runCommunityEnter = (pathname, id) => {
    const sendMessage = message => window.parentIFrame && window.parentIFrame.sendMessage(`${message}`, '*');
    if (pathname.includes('inbox')) {
        if (id) {
            sendMessage('communityIframeShouldResize');
        } else {
            window.iFrameResizer = {
                readyCallback: () => sendMessage('communityIframeShouldResize')
            };
        }
    } else if (pathname.includes('community')) {
        window.iFrameResizer = {
            readyCallback: () => sendMessage('communityIframeShouldResizeToDefault')
        };
    }
    Promise.resolve(actions.validateLinkedInToken()).catch(() => {});
};

// Renders a route's component, running its optional onEnter-style side effect
// whenever the matched path changes.
const RouteWithEnter = ({ component: Comp, enter, ...routeProps }) => {
    const pathname = routeProps.location.pathname;
    const id = routeProps.match && routeProps.match.params && routeProps.match.params.id;
    useEffect(() => {
        if (enter) {
            enter(pathname, id);
        }
    }, [pathname, id]);
    return <Comp {...routeProps} />;
};

// react-router 3's notFound route reloaded the page so the server can handle it.
const NotFoundReload = () => {
    useEffect(() => {
        window.location.reload();
    }, []);
    return <LoaderView />;
};

const routeList = [
    { path: routes.community, component: withTracking({ page: 'Community' })(withEmailLogin(MainCommunityContainerView)), enter: runCommunityEnter },
    { path: routes.person, component: withTracking({ page: 'Community Profile' })(MainUserProfileContainerView), enter: runCommunityEnter },
    { path: routes.inboxId, component: withTracking({ page: 'Community Inbox' })(MainInboxContainerView), enter: runCommunityEnter },
    { path: routes.inbox, component: withTracking({ page: 'Community Inbox' })(MainInboxContainerView), enter: runCommunityEnter },
    { path: routes.settings, component: withTracking({ page: 'User Settings' })(MainSettingsContainerView) },
    { path: routes.changeEmail, component: withTracking({ page: 'Change Email' })(MainChangeEmailContainerView) },
    { path: routes.changeEmailApproval, component: withTracking({ page: 'Approve Change Email' })(MainApproveChangeEmailContainerView) },
    { path: routes.speaker, component: withTracking({ page: 'Speaker' })(SpeakerContainer) },
    { path: routes.sessionPage, component: withTracking({ page: 'Session' })(withMe(SessionPageContainer)) },
    { path: routes.agenda, component: withTracking({ page: 'Agenda' })(withMe(AgendaContainer)) },
];

class MainContainer extends Component {
    componentDidMount() {
        saveUrlParamsToSessionStorage({
            windowLocationSearch: history.location.search,
            eventId: event?.id,
        });
        reactBackboneInit(store.dispatch, webSocket);
        const _loadCustomCode = (timeout = 1000) => {
            setTimeout(() => loadCustomCode({serverRendered: true}), timeout);
        };
        if (document.readyState !== 'loading') {
            _loadCustomCode(2000);
        } else {
            document.addEventListener('DOMContentLoaded', _loadCustomCode);
        }
        initializeSnowplowTrackerIfEnabled();
        actions.startSnowplowTrackingAction();

        // react-router 3 fired onChange on the parent route; replicate it with a
        // history listener. Scroll-to-top + back/forward scroll restoration
        // (formerly react-router-scroll) live in installScrollRestoration. Both
        // are cleaned up on unmount so they stay correct under StrictMode.
        this.unlisten = history.listen((location, action) => {
            // Agenda filter changes only rewrite the query string, so they are not
            // a route change: scrolling the host page of a widget embed to the top
            // and counting a pageview on every tag click would both be wrong. A POP
            // back onto such an entry is a real navigation, hence the action check.
            if (action !== 'POP' && location.state && location.state.preserveScroll) {
                return;
            }
            onRouteChange();
        });
        this.unlistenScroll = installScrollRestoration();
    }

    componentWillUnmount() {
        if (this.unlisten) {
            this.unlisten();
        }
        if (this.unlistenScroll) {
            this.unlistenScroll();
        }
    }

    render() {
        const mainReactClass = cls('main-react-view', {[template]: template });

        return (
            <div className={mainReactClass}>
                <Switch>
                    {routeList.map(route => (
                        <Route
                            key={route.path}
                            exact
                            path={`/${route.path}`}
                            render={props => (
                                <RouteWithEnter component={route.component} enter={route.enter} {...props} />
                            )}
                        />
                    ))}
                    <Route render={() => <NotFoundReload />} />
                </Switch>
            </div>
        );
    }
}

const AppRoutes = () => {
    useApplyCustomizations(event.id);

    return (
        <div data-iframe-height>
            <Provider store={store}>
                <Router history={history}>
                    <MainContainer />
                </Router>
            </Provider>
        </div>
    );
};

renderReact(document.getElementsByClassName('react-main')[0], <AppRoutes />);
